import { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";
import { Printer, FileText, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTIONS = ["pending_payment", "confirmed", "packed", "shipped", "delivered", "cancelled", "refunded"];

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [note, setNote] = useState("");

    const load = async () => {
        const params = filter !== "all" ? `?status=${filter}` : "";
        const r = await api.get(`/admin/orders${params}`);
        setOrders(r.data);
    };
    useEffect(() => { load(); }, [filter]);

    const updateStatus = async () => {
        if (!newStatus || !open) return;
        try {
            await api.post(`/admin/orders/${open.id}/status`, { status: newStatus, note });
            toast.success("Status updated");
            setOpen(null); setNote(""); setNewStatus("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        }
    };

    const exportRows = useMemo(
        () => orders.map((o) => ({
            order_number: o.order_number,
            customer: o.customer_name || o.address?.full_name,
            email: o.customer_email,
            items_count: o.items.length,
            total_inr: o.total / 100,
            status: o.status,
            payment_status: o.payment_status,
            shipping_city: o.address?.city,
            shipping_pincode: o.address?.pincode,
            awb: o.shiprocket?.awb_code,
            created_at: o.created_at,
        })),
        [orders]
    );

    const { page, pageSize, pageItems, setPage, setPageSize } = usePagination(orders, 20);
    const [selected, setSelected] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const toggle = (id) => {
        setSelected((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };
    const toggleAll = () => {
        if (selected.size === pageItems.length) setSelected(new Set());
        else setSelected(new Set(pageItems.map((o) => o.id)));
    };
    const labelUrl = (oid) => {
        const token = localStorage.getItem("dw_token") || "";
        return `${BACKEND_URL}/api/admin/labels/order/${oid}?t=${token.slice(-12)}`;
    };
    const printOne = async (oid) => {
        try {
            const resp = await api.get(`/admin/labels/order/${oid}`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const w = window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (err) {
            toast.error("Label generation failed");
        }
    };
    const printBulk = async () => {
        if (selected.size === 0) return toast.error("Select at least one order");
        setBulkBusy(true);
        try {
            const resp = await api.post(
                "/admin/labels/bulk",
                { order_ids: Array.from(selected) },
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `xplosiv-event-labels-${selected.size}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 30000);
            toast.success(`${selected.size} label(s) downloaded`);
        } catch (err) {
            toast.error("Bulk label generation failed");
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div data-testid="admin-orders-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="font-display text-4xl">Orders</h1>
                    <p className="text-muted-foreground mt-1">{orders.length} orders</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {selected.size > 0 && (
                        <button
                            onClick={printBulk}
                            disabled={bulkBusy}
                            className="dw-btn-primary inline-flex items-center gap-1.5 px-4 h-10 text-xs font-mono-ui"
                            data-testid="orders-print-bulk-btn"
                        >
                            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
                            Print {selected.size} label{selected.size === 1 ? "" : "s"}
                        </button>
                    )}
                    <ExportButtons rows={exportRows} filename={`xplosiv_bookings_${filter}`} />
                    <Select value={filter} onValueChange={setFilter}>
                        <SelectTrigger className="w-[200px] rounded-full bg-white border-[#E7DED2]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-4">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="admin-orders-table">
                            <thead>
                                <tr className="text-left text-xs font-mono-ui text-muted-foreground border-b border-[#E7DED2]">
                                    <th className="py-3 w-8">
                                        <input
                                            type="checkbox"
                                            checked={pageItems.length > 0 && selected.size === pageItems.length}
                                            onChange={toggleAll}
                                            data-testid="orders-select-all"
                                        />
                                    </th>
                                    <th>Order</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Date</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map(o => (
                                    <tr key={o.id} className="border-b border-[#F4EFE6]" data-testid={`admin-order-row-${o.order_number}`}>
                                        <td className="py-3">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(o.id)}
                                                onChange={() => toggle(o.id)}
                                                data-testid={`orders-select-${o.order_number}`}
                                            />
                                        </td>
                                        <td className="py-3 font-medium">{o.order_number}</td>
                                        <td>
                                            <div>{o.customer_name || o.address?.full_name}</div>
                                            <div className="text-xs text-muted-foreground">{o.customer_email}</div>
                                        </td>
                                        <td>{o.items.length}</td>
                                        <td>{formatPrice(o.total)}</td>
                                        <td><span className="text-xs font-mono-ui px-2 py-1 rounded-full bg-[#F4EFE6]">{o.status.replace("_", " ")}</span></td>
                                        <td className="text-muted-foreground text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                                        <td className="text-right space-x-1.5 whitespace-nowrap">
                                            <button
                                                onClick={() => printOne(o.id)}
                                                title="Print shipping label"
                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-[11px] font-mono-ui"
                                                data-testid={`orders-print-label-${o.order_number}`}
                                            >
                                                <Printer className="h-3 w-3" /> Label
                                            </button>
                                            <button onClick={() => { setOpen(o); setNewStatus(o.status); }} className="text-xs font-mono-ui underline" data-testid={`admin-manage-${o.order_number}`}>Manage</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} pageSize={pageSize} total={orders.length} onPage={setPage} onPageSize={setPageSize} />
                </CardContent>
            </Card>

            <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
                <DialogContent className="rounded-[20px]">
                    <DialogHeader><DialogTitle className="font-display text-2xl">Order {open?.order_number}</DialogTitle></DialogHeader>
                    {open && (
                        <div className="space-y-3">
                            <p className="text-sm"><span className="font-mono-ui text-muted-foreground">Customer:</span> {open.customer_name} · {open.customer_email}</p>
                            <p className="text-sm"><span className="font-mono-ui text-muted-foreground">Total:</span> {formatPrice(open.total)}</p>
                            <p className="text-sm"><span className="font-mono-ui text-muted-foreground">Address:</span> {open.address.line1}, {open.address.city} — {open.address.pincode}</p>
                            <p className="text-sm"><span className="font-mono-ui text-muted-foreground">Current status:</span> {open.status.replace("_", " ")}</p>
                            <div className="max-h-40 overflow-y-auto space-y-2">
                                {open.items.map((it, i) => (
                                    <div key={i} className="flex gap-3 items-center text-xs">
                                        <img src={it.image} alt="" className="h-10 w-10 rounded-lg object-cover" />
                                        <span className="flex-1">{it.product_title} — {it.color}/{it.size} ×{it.qty}</span>
                                        <span>{formatPrice(it.subtotal)}</span>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <p className="font-mono-ui mb-2">Update status</p>
                                <Select value={newStatus} onValueChange={setNewStatus}>
                                    <SelectTrigger className="rounded-2xl"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <Textarea placeholder="Internal note (optional)" value={note} onChange={e => setNote(e.target.value)} className="rounded-2xl" />
                            <button onClick={updateStatus} className="w-full dw-btn-primary h-11 font-mono-ui" data-testid="admin-order-update-status">Update order</button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
